import { useState, useCallback, useRef } from 'react';
import { aiAssistantDemoService } from '@/services/demo/ai-assistant-demo.service';
import { getChatAssistantAgentId } from '@/lib/agent-config';
import { useStreamingChat } from '@/hooks/use-streaming-chat';
import type { AIMessage, AIMessageUIBlock, ToolCall } from '@/types/ai-assistant.types';
import type { AgentChatResponse } from '@/types/streaming.types';

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

interface UseAIAssistantChatOptions {
  isDemo?: boolean;
  onMessageReceived?: (message: AIMessage) => void;
  onError?: (error: Error) => void;
  onBackgroundResponse?: (sessionId: string, message: AIMessage) => void;
  getCurrentSessionId?: () => string | null;
}

interface UseAIAssistantChatReturn {
  messages: AIMessage[];
  isProcessing: boolean;
  currentTool: ToolCall | undefined;
  error: string | null;
  sendMessage: (content: string, context?: string) => Promise<void>;
  clearHistory: () => void;
  setMessages: React.Dispatch<React.SetStateAction<AIMessage[]>>;
  cancelProcessing: () => void;
  /** Reset the Agent Builder conversation ID (call on session switch). */
  resetConversation: () => void;
  /** Text being streamed from the agent (empty when not streaming). */
  streamingText: string;
  /** Agent thinking / processing message during streaming. */
  thinkingMessage: string | null;
}

const STORAGE_KEY_PREFIX = 'ai-assistant-session-';
const DEFAULT_STORAGE_KEY = 'ai-assistant-messages';

function getStorageKey(sessionId: string | null): string {
  return sessionId ? `${STORAGE_KEY_PREFIX}${sessionId}` : DEFAULT_STORAGE_KEY;
}

function loadMessagesFromStorage(sessionId: string | null = null): AIMessage[] {
  try {
    const stored = localStorage.getItem(getStorageKey(sessionId));
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveMessagesToStorage(messages: AIMessage[], sessionId: string | null = null): void {
  try {
    const key = getStorageKey(sessionId);
    if (messages.length > 0) {
      localStorage.setItem(key, JSON.stringify(messages));
    } else {
      localStorage.removeItem(key);
    }
  } catch {
    // Ignore storage errors
  }
}

function deleteSessionFromStorage(sessionId: string): void {
  try {
    localStorage.removeItem(getStorageKey(sessionId));
  } catch {
    // Ignore storage errors
  }
}

// Export storage utilities for use in context
export { loadMessagesFromStorage, saveMessagesToStorage, deleteSessionFromStorage };

/** Build an AIMessage from the streaming response. */
function buildAssistantMessageFromStream(
  response: AgentChatResponse,
): { message: AIMessage; toolCall?: ToolCall } {
  const assistantMessage: AIMessage = {
    id: generateId(),
    role: 'assistant',
    content: response.response,
    timestamp: Date.now(),
  };

  if (response.tools_used.length > 0) {
    assistantMessage.toolsUsed = response.tools_used;
  }

  if (response.execution_details && response.execution_details.length > 0) {
    const uiBlocks = response.execution_details
      .filter((d) => d.status === 'success')
      .map((d, idx) => ({
        id: `exec-${idx}-${Date.now()}`,
        component: 'tool-result',
        props: {
          operationName: d.operation_name,
          status: d.status,
          durationMs: d.duration_ms,
        } as Readonly<Record<string, unknown>>,
      }));
    if (uiBlocks.length > 0) {
      assistantMessage.uiBlocks = uiBlocks as AIMessageUIBlock[];
    }
  }

  return { message: assistantMessage, toolCall: undefined };
}

/** Build an AIMessage from the demo service response. */
function buildAssistantMessageFromDemo(
  response: { id: string; content: string; timestamp: string; toolCall?: { id: string; name: string; status: string; result?: unknown }; uiBlocks?: unknown[]; toolsUsed?: string[] },
): { message: AIMessage; toolCall?: ToolCall } {
  const assistantMessage: AIMessage = {
    id: response.id,
    role: 'assistant',
    content: response.content,
    timestamp: new Date(response.timestamp).getTime(),
  };

  let parsedToolCall: ToolCall | undefined;

  if (response.toolCall) {
    parsedToolCall = {
      id: response.toolCall.id,
      name: response.toolCall.name,
      status: response.toolCall.status as ToolCall['status'],
      result: response.toolCall.result,
    };
    assistantMessage.toolCall = parsedToolCall;
  }

  if (response.uiBlocks && response.uiBlocks.length > 0) {
    assistantMessage.uiBlocks = response.uiBlocks as AIMessageUIBlock[];
  }

  if (response.toolsUsed && response.toolsUsed.length > 0) {
    assistantMessage.toolsUsed = response.toolsUsed;
  }

  return { message: assistantMessage, toolCall: parsedToolCall };
}

export function useAIAssistantChat(
  options: UseAIAssistantChatOptions = {}
): UseAIAssistantChatReturn {
  const { isDemo = false, onMessageReceived, onError, onBackgroundResponse, getCurrentSessionId } = options;

  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentTool, setCurrentTool] = useState<ToolCall | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const requestSessionIdRef = useRef<string | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  const demoAbortRef = useRef<AbortController | null>(null);

  const {
    streamingState,
    startConversation,
    continueConversation,
    cancelStreaming,
  } = useStreamingChat({
    onError: (err) => {
      onError?.(err);
    },
  });

  const appendAssistantMessage = useCallback((
    assistantMessage: AIMessage,
    requestSessionId: string | null,
  ): void => {
    const currentSessionId = getCurrentSessionId?.() ?? null;
    const sessionChanged = requestSessionId !== currentSessionId;

    if (sessionChanged && requestSessionId) {
      onBackgroundResponse?.(requestSessionId, assistantMessage);
    } else {
      setMessages(prev => {
        const updated = [...prev, assistantMessage];
        saveMessagesToStorage(updated, currentSessionId);
        return updated;
      });
      onMessageReceived?.(assistantMessage);
    }
  }, [getCurrentSessionId, onBackgroundResponse, onMessageReceived]);

  const handleDemoMessage = useCallback(async (
    content: string,
    requestSessionId: string | null,
  ): Promise<void> => {
    demoAbortRef.current = new AbortController();
    const response = await aiAssistantDemoService.sendMessage(
      { message: content },
      demoAbortRef.current.signal,
    );
    const { message: assistantMessage, toolCall: parsedToolCall } =
      buildAssistantMessageFromDemo(response);

    if (parsedToolCall) {
      setCurrentTool(parsedToolCall);
    }

    appendAssistantMessage(assistantMessage, requestSessionId);
  }, [appendAssistantMessage]);

  const handleStreamingMessage = useCallback(async (
    content: string,
    requestSessionId: string | null,
  ): Promise<void> => {
    const agentId = getChatAssistantAgentId();
    const existingConversationId = conversationIdRef.current;

    const result = existingConversationId
      ? await continueConversation(existingConversationId, agentId, content)
      : await startConversation(agentId, content);

    if (result.conversationId) {
      conversationIdRef.current = result.conversationId;
    }

    if (result.response) {
      const { message: assistantMessage, toolCall: parsedToolCall } =
        buildAssistantMessageFromStream(result.response);

      if (parsedToolCall) {
        setCurrentTool(parsedToolCall);
      }

      appendAssistantMessage(assistantMessage, requestSessionId);
      return;
    }

    const fallbackContent =
      streamingState.currentText || 'The agent did not return a response.';
    const fallbackMessage: AIMessage = {
      id: generateId(),
      role: 'assistant',
      content: fallbackContent,
      timestamp: Date.now(),
    };
    appendAssistantMessage(fallbackMessage, requestSessionId);
  }, [appendAssistantMessage, startConversation, continueConversation, streamingState.currentText]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sendMessage = useCallback(async (content: string, _context?: string) => {
    if (!content.trim()) return;

    const requestSessionId = getCurrentSessionId?.() ?? null;
    requestSessionIdRef.current = requestSessionId;

    const userMessage: AIMessage = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      timestamp: Date.now(),
    };

    setMessages(prev => {
      const updated = [...prev, userMessage];
      saveMessagesToStorage(updated, requestSessionId);
      return updated;
    });
    setIsProcessing(true);
    setError(null);

    try {
      const trimmedContent = content.trim();

      if (isDemo) {
        await handleDemoMessage(trimmedContent, requestSessionId);
      } else {
        await handleStreamingMessage(trimmedContent, requestSessionId);
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }

      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      onError?.(err instanceof Error ? err : new Error(errorMessage));

      const errorResponse: AIMessage = {
        id: generateId(),
        role: 'assistant',
        content: 'I apologize, but I encountered an error processing your request. Please try again.',
        timestamp: Date.now(),
      };

      const errorSessionId = getCurrentSessionId?.() ?? null;
      setMessages(prev => {
        const updated = [...prev, errorResponse];
        saveMessagesToStorage(updated, errorSessionId);
        return updated;
      });
    } finally {
      setIsProcessing(false);
      setCurrentTool(undefined);
    }
  }, [isDemo, onError, getCurrentSessionId, handleDemoMessage, handleStreamingMessage]);

  const clearHistory = useCallback(() => {
    const sessionId = getCurrentSessionId?.() ?? null;
    setMessages([]);
    saveMessagesToStorage([], sessionId);
    setError(null);
    setCurrentTool(undefined);
    conversationIdRef.current = null;
  }, [getCurrentSessionId]);

  const resetConversation = useCallback(() => {
    conversationIdRef.current = null;
  }, []);

  const cancelProcessing = useCallback(() => {
    cancelStreaming();
    if (demoAbortRef.current) {
      demoAbortRef.current.abort();
      demoAbortRef.current = null;
    }
    setIsProcessing(false);
    setCurrentTool(undefined);
  }, [cancelStreaming]);

  return {
    messages,
    isProcessing,
    currentTool,
    error,
    sendMessage,
    clearHistory,
    setMessages,
    cancelProcessing,
    resetConversation,
    streamingText: streamingState.currentText,
    thinkingMessage: streamingState.thinkingMessage,
  };
}
